import { useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, Settings, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { usePreferencesStore } from '../../store/preferencesStore';
import { cn } from '../../utils/cn';
import { HOME_ITEM, FILES_ITEM, PRICING_ITEM, AI_TOOLS_ITEMS, type NavItem } from './navConfig';

const PANEL_BG = 'bg-[#16171e]';

function SectionLabel({ title }: { title: string }) {
  return (
    <p className="mb-1 px-3 pt-3 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
      {title}
    </p>
  );
}

/** A nav row in the drawer — min-44px tap target, closes the drawer on tap. */
function DrawerLink({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-xl px-3 min-h-[44px] py-2.5 text-sm font-medium transition-colors',
          isActive
            ? 'bg-brand-600/90 text-white shadow-sm'
            : 'text-zinc-300 hover:bg-white/[0.06] hover:text-zinc-100',
        )
      }
    >
      {item.icon}
      <span>{item.label}</span>
    </NavLink>
  );
}

export function MobileDrawer() {
  const isOpen = usePreferencesStore((s) => s.mobileNavOpen);
  const closeMobileNav = usePreferencesStore((s) => s.closeMobileNav);

  const { user, isSuperAdmin, logout } = useAuth();

  const location = useLocation();

  // Close the drawer whenever the route changes.
  useEffect(() => {
    closeMobileNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Escape to close + body scroll lock while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMobileNav(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, closeMobileNav]);

  const handleLogout = async () => {
    closeMobileNav();
    await logout();
  };

  if (!user) return null;

  const close = () => closeMobileNav();

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={close}
          />

          {/* Panel */}
          <motion.aside
            className={cn(
              'absolute left-0 top-0 bottom-0 flex w-[280px] max-w-[85vw] flex-col',
              PANEL_BG,
            )}
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 34, mass: 0.9 }}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            {/* Brand + close */}
            <div className="flex h-14 flex-shrink-0 items-center justify-between gap-3 border-b border-white/[0.06] px-4">
              <Link to="/dashboard" onClick={close} aria-label="Go to home" className="flex items-center gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-electric-500 to-neon-cyan shadow-lg">
                  <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0112 20.055a11.952 11.952 0 01-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  </svg>
                </div>
                <span className="truncate text-sm font-semibold text-white">
                  Sci<span className="bg-gradient-to-r from-electric-400 to-neon-cyan bg-clip-text text-transparent">Campus</span>
                </span>
              </Link>
              <button
                onClick={close}
                aria-label="Close menu"
                className="tap-target -mr-2 flex items-center justify-center rounded-lg text-zinc-400 hover:bg-white/[0.06] hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2.5 py-3 space-y-3 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-zinc-700">
              <DrawerLink item={HOME_ITEM} onNavigate={close} />

              <div>
                <SectionLabel title="AI" />
                {AI_TOOLS_ITEMS.map((item) => (
                  <DrawerLink key={item.to} item={item} onNavigate={close} />
                ))}
              </div>

              <div>
                <SectionLabel title="Storage" />
                <DrawerLink item={FILES_ITEM} onNavigate={close} />
              </div>

              <div>
                <SectionLabel title="Plan" />
                <DrawerLink item={PRICING_ITEM} onNavigate={close} />
              </div>
            </nav>

            {/* Footer: profile / logout / settings */}
            <div className="flex-shrink-0 border-t border-white/[0.06] px-2.5 py-2.5 space-y-0.5">
              <div className="flex items-center gap-1">
                <Link
                  to="/profile"
                  onClick={close}
                  className="flex flex-1 items-center gap-3 rounded-xl px-2.5 py-2 hover:bg-white/[0.06] transition-colors overflow-hidden"
                >
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white shadow-sm">
                    {user.firstName[0]}{user.lastName[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white">{user.fullName}</p>
                    <p className="truncate text-xs text-zinc-500">{isSuperAdmin ? 'Super Admin' : 'Member'}</p>
                  </div>
                </Link>
                <button
                  onClick={handleLogout}
                  title="Sign out"
                  className="tap-target flex flex-shrink-0 items-center justify-center rounded-lg text-zinc-600 hover:text-red-400 hover:bg-white/[0.06] transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
              <NavLink
                to="/settings"
                onClick={close}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-xl px-2.5 min-h-[44px] py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-brand-600/90 text-white shadow-sm'
                      : 'text-zinc-500 hover:bg-white/[0.06] hover:text-zinc-200',
                  )
                }
              >
                <Settings className="h-4 w-4 flex-shrink-0" />
                Settings
              </NavLink>
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
